/**
 * Cross-platform geolocation wrapper.
 *
 * Routes to Capacitor's native Geolocation plugin when running inside
 * the Android / iOS WebView (so we get true OS permission dialogs +
 * settings deep-link), and falls back to ``navigator.geolocation`` in
 * the browser / PWA. Returns a uniform shape so callers don't have
 * to fork their code:
 *
 *   ``{ state: "granted"|"denied"|"prompt"|"unsupported"|"error",
 *       coords?: {lat, lng, accuracy},  // present only when granted
 *       error?: string }``
 *
 * Play Store / App Store policy compliance:
 *   * We never call ``getCurrentPosition`` blind. The helper first
 *     reads the permission state via ``checkPermissions`` so callers
 *     can show an in-context rationale modal BEFORE the OS prompt
 *     fires for the first time.
 *   * On denied/blocked states we expose an ``openSettings()`` deep
 *     link so the user can flip the toggle without leaving the app.
 */
import { Capacitor } from "@capacitor/core";

// iter54 diagnostics — every step of the location flow logs a tagged
// message to console + (when ``window.__geo_dbg`` is set) appends to
// a ring buffer that surfaces in the toast on failure. Lets us trace
// "current location does nothing" reports remotely via
// ``adb logcat | grep BIZIL-GEO``.
function _dbg(stage, payload = {}) {
    try {
        const line = `[BIZIL-GEO] ${stage} ${JSON.stringify(payload)}`;

        console.log(line);
        if (typeof window !== "undefined") {
            window.__geo_dbg = window.__geo_dbg || [];
            window.__geo_dbg.push({ t: Date.now(), stage, ...payload });
            if (window.__geo_dbg.length > 30) window.__geo_dbg.shift();
        }
    } catch { /* logging must never throw */ }
}

/** Race ``promise`` against a per-call hard timeout as a belt-and-braces
 *  guard against a plugin call that never settles. Resolves with the
 *  timeout sentinel on expiry; never throws. */
async function _withTimeout(promise, ms, label) {
    let timer;
    const sentinel = { __timedOut: true, label };
    try {
        return await Promise.race([
            promise,
            new Promise((resolve) => { timer = setTimeout(() => resolve(sentinel), ms); }),
        ]);
    } finally {
        if (timer) clearTimeout(timer);
    }
}

/**
 * Attempt to load the native ``@capacitor/geolocation`` plugin.
 * Returns ``null`` on web/PWA, or when the plugin isn't compiled into
 * the running APK/IPA. Callers fall through to the WebView's built-in
 * ``navigator.geolocation`` in that case.
 *
 * Returns the plugin wrapped as ``{ plugin }`` — NEVER the bare plugin
 * object. Capacitor's native-bridge proxy answers "yes, callable" to
 * ANY property probe (it has no fixed method list on the JS side), so
 * returning the raw proxy as an ``async`` function's return value makes
 * the JS engine's implicit ``Promise.resolve(returnValue)`` duck-type
 * it as a thenable and invoke ``proxy.then(resolve, reject)`` — a real
 * bridge call to a non-existent native method "then". Confirmed via
 * on-device adb logcat (this function used to ``return mod.Geolocation``
 * directly):
 *   [BIZIL-GEO] checkLocationPermission.enter {}
 *   [BIZIL-GEO] native.imported {"ok":true}
 *   Uncaught (in promise) Error: "Geolocation.then()" is not implemented on android
 * — the crash fires the instant ``checkLocationPermission()`` awaits
 * this function's result, BEFORE ``checkPermissions()`` is ever
 * actually called on the plugin. That's proof the plugin loads fine;
 * the earlier "native plugin isn't compiled in" / "wedges the JS
 * thread" theories were misdiagnoses of this exact bug, which is what
 * drove several defensive rewrites to bypass the native plugin
 * entirely in favour of ``navigator.geolocation``. That bypass is
 * itself the cause of "permission already granted but the app still
 * shows the request dialog": Android's WebView does NOT automatically
 * inherit the app's OS-level (ACCESS_FINE_LOCATION) grant for
 * ``navigator.geolocation`` — only the real native plugin does. Same
 * footgun already fixed for the BatteryOptimization plugin in
 * ``capacitor.js:_batteryPlugin()`` — apply the identical wrapping here.
 */
async function _loadNativePlugin() {
    if (!Capacitor.isNativePlatform()) {
        _dbg("native.skipped.web");
        return null;
    }
    try {
        const mod = await import("@capacitor/geolocation");
        const ok = !!mod?.Geolocation?.checkPermissions;
        _dbg("native.imported", { ok });
        if (ok) return { plugin: mod.Geolocation };
    } catch (e) {
        _dbg("native.import-failed", { msg: String(e?.message || e) });
    }
    return null;
}

/** Browser/WebView-path helper. Used only on real web/PWA — native
 *  Android/iOS always goes through the Capacitor plugin above, since
 *  the WebView's own ``navigator.geolocation`` / Permissions API do
 *  NOT reflect the app's OS-level location permission. */
async function _checkBrowserPermission() {
    _dbg("browser.checkPerm.enter");
    if (!navigator.geolocation) { _dbg("browser.checkPerm.no-geo"); return "unsupported"; }
    if (!navigator.permissions?.query) { _dbg("browser.checkPerm.no-perms-api"); return "prompt"; }
    try {
        const status = await navigator.permissions.query({ name: "geolocation" });
        _dbg("browser.checkPerm.ok", { state: status.state });
        return status.state;
    } catch (e) {
        _dbg("browser.checkPerm.err", { msg: String(e?.message || e) });
        return "prompt";
    }
}

function _browserGetPosition(enableHighAccuracy, timeoutMs) {
    _dbg("browser.getPos.start", { enableHighAccuracy, timeoutMs });
    return new Promise((resolve) => {
        if (!navigator.geolocation) {
            _dbg("browser.getPos.no-geolocation");
            return resolve({ state: "unsupported" });
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                _dbg("browser.getPos.ok", { lat: pos.coords.latitude, lng: pos.coords.longitude });
                resolve({
                    state: "granted",
                    coords: {
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                        accuracy: pos.coords.accuracy,
                    },
                });
            },
            (err) => {
                _dbg("browser.getPos.err", { code: err?.code, msg: err?.message });
                if (err?.code === err?.PERMISSION_DENIED) return resolve({ state: "denied" });
                if (err?.code === err?.TIMEOUT) return resolve({ state: "error", error: "Timed out" });
                resolve({ state: "error", error: err?.message || "Location unavailable" });
            },
            { enableHighAccuracy, timeout: timeoutMs, maximumAge: 60000 },
        );
    });
}

/** @returns {"granted"|"denied"|"prompt"|"unsupported"} */
export async function checkLocationPermission() {
    _dbg("checkLocationPermission.enter");
    const native = await _loadNativePlugin();
    if (native) {
        // Backward-compat: this app ships via Capacitor live-reload
        // (``server.url``), so this JS goes out to EVERY installed
        // build the instant it deploys — including old Android/iOS
        // binaries built before ``@capacitor/geolocation``'s native
        // module was compiled in. On those, the JS import above still
        // resolves fine (it's just a JS proxy), but the ACTUAL bridge
        // call below rejects with a native "not implemented" error.
        // The whole call — not just parsing its result — must be
        // inside this try so that rejection can't escape as an
        // uncaught exception; old builds fall back to the browser
        // Permissions API instead, same as ``_loadNativePlugin``
        // returning ``null`` already does for old-web/no-plugin cases.
        try {
            const res = await _withTimeout(native.plugin.checkPermissions(), 4000, "checkPermissions");
            if (res && res.__timedOut) {
                _dbg("native.checkPerm.timeout");
                // Honest "unknown" fallback rather than assuming granted.
                // ``requestLocationPermission()`` / ``getCurrentLocation()``
                // are the ground truth and behave correctly regardless —
                // Android skips the OS dialog entirely when permission is
                // already granted, so returning "prompt" here costs nothing
                // beyond one extra (silent) permission check.
                return "prompt";
            }
            _dbg("native.checkPerm.ok", { res });
            const fine = res?.location;
            const coarse = res?.coarseLocation;
            const s = (fine === "granted" || coarse === "granted") ? "granted"
                : (fine || coarse || "prompt");
            return s === "prompt-with-rationale" ? "prompt" : s;
        } catch (e) {
            _dbg("native.checkPerm.err", { msg: String(e?.message || e) });
            return _checkBrowserPermission();
        }
    }
    return _checkBrowserPermission();
}

/**
 * Trigger the native OS permission prompt. Requests BOTH ``location``
 * (fine/GPS) and ``coarseLocation`` (cell + Wi-Fi triangulation) so the
 * network-first ``getCurrentLocation`` path works on devices where the
 * user grants only Approximate. Android skips the dialog entirely and
 * resolves instantly when permission is already granted, or when the
 * user has previously permanently denied it — callers should treat any
 * non-"granted" result the same way (offer ``openAppSettings()``).
 *
 * On success we immediately follow up with a network-first position
 * fetch so the caller gets coordinates in the SAME round trip instead
 * of needing a second "Current location" tap.
 *
 * @returns {Promise<{state, coords?, error?}>}
 */
export async function requestLocationPermission() {
    _dbg("requestLocationPermission.enter");
    const native = await _loadNativePlugin();
    if (native) {
        // Backward-compat: see the matching comment in
        // ``checkLocationPermission()`` — this whole block, including
        // the initial bridge call, must stay inside the try so an old
        // build without the native module compiled in can't crash;
        // it falls back to the browser's permission-prompting
        // ``getCurrentPosition`` call instead.
        try {
            const raceResult = await _withTimeout(
                native.plugin.requestPermissions({ permissions: ["location", "coarseLocation"] }),
                15000, "requestPermissions",
            );
            if (raceResult && raceResult.__timedOut) {
                _dbg("native.requestPerm.timeout");
                return { state: "error", error: "Permission request timed out" };
            }
            const fine = raceResult?.location;
            const coarse = raceResult?.coarseLocation;
            let state = (fine === "granted" || coarse === "granted") ? "granted"
                : (fine || coarse || "denied");
            if (state === "prompt-with-rationale") state = "prompt";
            _dbg("native.requestPerm.ok", { raceResult, state });
            if (state !== "granted") return { state };
            // Permission is resolved at this point, so this can't
            // trigger a second concurrent OS dialog — safe to also
            // retry with GPS on a network-position failure, same as
            // getCurrentLocation().
            const first = await _nativeGetPosition(native.plugin, false, 8000);
            if (first.state === "granted" || first.state === "denied") return first;
            return _nativeGetPosition(native.plugin, true, 15000);
        } catch (e) {
            _dbg("native.requestPerm.err", { msg: String(e?.message || e) });
            return _browserGetPosition(false, 8000);
        }
    }
    // Web / PWA — the browser's own getCurrentPosition() call IS the
    // permission prompt; a successful callback conveniently already
    // carries the coords.
    return _browserGetPosition(false, 8000);
}

/**
 * High-level helper used by callers: returns coords if everything works,
 * else a structured error so the caller can show the right UI.
 *
 *   const r = await getCurrentLocation();
 *   if (r.coords) {...} else if (r.state === "denied") {showRationale()}
 *
 * Capacitor native vs browser behaviour:
 *   * Native — we call the @capacitor/geolocation plugin. We try
 *     **network-first** (``enableHighAccuracy=false``, ~8s) because
 *     GPS-first calls routinely time out indoors (the antenna takes
 *     30+s for a cold lock). If the network position fails or times
 *     out (but wasn't a permission denial), we retry once with GPS at
 *     a longer ~15s timeout.
 *   * Browser / PWA — we use ``navigator.geolocation`` directly with
 *     the caller's requested settings.
 */
export async function getCurrentLocation({
    enableHighAccuracy = true, timeoutMs = 20000,
} = {}) {
    _dbg("getCurrentLocation.enter", { enableHighAccuracy, timeoutMs });
    // Hard outer wrapper so a plugin call that somehow never settles
    // can't leave the caller's button spinner stuck forever. Sized to
    // comfortably fit one network-first attempt + one GPS retry
    // (8s+3s grace, then 15s+3s grace ≈ 29s worst case).
    const hardLimit = 30_000;
    let timeoutId;
    const watchdog = new Promise((resolve) => {
        timeoutId = setTimeout(() => {
            _dbg("getCurrentLocation.watchdog-fired", { ms: hardLimit });
            resolve({
                state: "error",
                error: "Location is taking too long. Try again outdoors or near a window.",
            });
        }, hardLimit);
    });
    try {
        return await Promise.race([_getCurrentLocationInner({
            enableHighAccuracy, timeoutMs,
        }), watchdog]);
    } finally {
        if (timeoutId) clearTimeout(timeoutId);
    }
}

async function _getCurrentLocationInner({ enableHighAccuracy, timeoutMs }) {
    const native = await _loadNativePlugin();
    if (!native) {
        // Web / PWA — no native plugin to route through.
        _dbg("getCurrentLocation.browser-fallback");
        return _browserGetPosition(enableHighAccuracy, timeoutMs);
    }
    // Check permission BEFORE ever calling getCurrentPosition(). On
    // Android, getCurrentPosition() triggers the raw OS permission
    // dialog itself as a side effect when permission hasn't been
    // decided yet — confirmed on-device via adb logcat: calling it
    // blind popped a real GrantPermissionsActivity, which then sat
    // waiting on a human tap while our (much shorter) JS timeout fired
    // and this function's own GPS-retry logic issued a SECOND
    // concurrent getCurrentPosition() call — Android flatly rejects
    // that ("Can request only one set of permissions at a time"),
    // poisoning both attempts and leaving the user staring at a system
    // dialog the app had already given up on.
    //
    // This helper is the "quick fetch, assumes already granted" path
    // used by every "Current location" button — it must NEVER surface
    // the OS dialog itself. When permission isn't granted yet it
    // returns {state:"prompt"|"denied"} so the caller shows the
    // in-context rationale modal FIRST (Play Store policy — see file
    // docstring), whose "Continue" button calls
    // requestLocationPermission() to deliberately trigger the OS
    // dialog with a timeout budget sized for a human to answer it.
    const permState = await checkLocationPermission();
    if (permState !== "granted") {
        _dbg("getCurrentLocation.not-granted", { permState });
        return { state: permState === "denied" ? "denied" : "prompt" };
    }
    const first = await _nativeGetPosition(native.plugin, false, 8000);
    if (first.state === "granted" || first.state === "denied") return first;
    // Network position failed/timed out (but wasn't a permission
    // denial) — retry once with real GPS before giving up.
    _dbg("getCurrentLocation.retry-gps");
    return _nativeGetPosition(native.plugin, true, 15000);
}

/** Single attempt against the Capacitor native plugin with explicit
 *  accuracy + timeout. Always resolves with a structured result —
 *  never throws — so the orchestrator above can decide whether to
 *  retry, escalate, or surface the error. Always returns a plain
 *  object literal (never the plugin proxy itself), so it never risks
 *  the thenable footgun documented on ``_loadNativePlugin`` above. */
async function _nativeGetPosition(Geo, enableHighAccuracy, timeoutMs) {
    _dbg("native.getPos.start", { enableHighAccuracy, timeoutMs });
    // Backward-compat: the initial bridge call itself (not just
    // parsing its result) must be inside this try — on an old
    // installed build without the native module compiled in it
    // rejects immediately with a native "not implemented" error, which
    // would otherwise escape uncaught (see the matching comment on
    // ``checkLocationPermission()``). Caught below and reported as a
    // normal structured error, honouring this function's own contract
    // of never throwing.
    try {
        // Belt-and-braces JS-side timeout on top of the plugin's own
        // ``timeout`` option, in case the bridge call never settles.
        const raceResult = await _withTimeout(
            Geo.getCurrentPosition({
                enableHighAccuracy, timeout: timeoutMs, maximumAge: 60000,
            }),
            timeoutMs + 3000, "getCurrentPosition");
        if (raceResult && raceResult.__timedOut) {
            _dbg("native.getPos.js-timeout");
            return { state: "error", error: "Location request timed out" };
        }
        const pos = raceResult;
        _dbg("native.getPos.ok", {
            lat: pos.coords.latitude, lng: pos.coords.longitude,
            acc: pos.coords.accuracy,
        });
        return {
            state: "granted",
            coords: {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
            },
        };
    } catch (e) {
        const msg = String(e?.message || e || "").toLowerCase();
        _dbg("native.getPos.err", { msg });
        if (msg.includes("denied") || msg.includes("not granted")) {
            return { state: "denied" };
        }
        if (msg.includes("disabled") || msg.includes("not enabled")
            || msg.includes("location services")) {
            return { state: "denied", error: "Location services are turned off. Enable Location in your device Settings." };
        }
        if (msg.includes("timeout") || msg.includes("timed out")) {
            return { state: "error", error: "Location request timed out" };
        }
        return { state: "error", error: e?.message || "Location unavailable" };
    }
}

/**
 * Best-effort deep link into the device's app-info settings page so
 * the user can flip the location permission. On the web we fall
 * through to a no-op (the rationale modal still shows guidance text).
 *
 * History of two failed attempts here, both confirmed broken by
 * actually testing on a connected device rather than assuming:
 *   1. ``@capacitor-community/native-settings`` via a webpack-bypassed
 *      runtime ``import()`` string — package was never actually
 *      installed (not in package.json, node_modules, or the compiled
 *      native project), so the import failed every time.
 *   2. ``@capacitor/app-launcher``'s ``openUrl()`` — this plugin can
 *      only construct a generic ``android.intent.action.VIEW`` intent.
 *      Confirmed via ``adb shell am start -a android.intent.action.VIEW
 *      -d "package:<pkg>"`` on a real device: "Error: Activity not
 *      started, unable to resolve Intent". Android's Settings app only
 *      registers a handler for the SPECIFIC named action
 *      ``android.settings.APPLICATION_DETAILS_SETTINGS`` — confirmed
 *      working via the same adb command with that action instead. No
 *      URL string passed through ``openUrl()`` can produce that; it
 *      needs an actual native Intent with the correct action.
 *
 * Fixed with a small custom native plugin (``AppSettingsPlugin``,
 * baked in by ``scripts/_build_common.py:patch_app_settings_plugin`` —
 * same established pattern as the existing ``BatteryOptimizationPlugin``)
 * that fires the correct Intent directly. REQUIRES A NATIVE REBUILD —
 * this is genuinely native Java code, not something a JS-only change
 * or app reload can deliver.
 */
export async function openAppSettings() {
    if (!Capacitor.isNativePlatform()) return false;
    const platform = Capacitor.getPlatform();
    if (platform === "ios") {
        try {
            // iOS has no equivalent "wrong intent action" problem —
            // ``app-settings:`` is a genuine URL scheme the OS resolves
            // directly, so the AppLauncher path is fine here.
            const { AppLauncher } = await import("@capacitor/app-launcher");
            const r = await AppLauncher.openUrl({ url: "app-settings:" });
            return !!(r && r.completed);
        } catch {
            return false;
        }
    }
    try {
        // Wrapped in ``{ plugin }`` rather than awaited/returned bare —
        // registerPlugin()'s raw native-bridge proxy answers "yes,
        // callable" to a bare ``.then`` probe, so awaiting the PROXY
        // itself (not a method call on it) makes the JS engine's
        // thenable duck-typing invoke it AS a plugin call. Same
        // precaution ``_batteryPlugin()`` in capacitor.js already takes.
        const { registerPlugin } = await import("@capacitor/core");
        const plugin = registerPlugin("AppSettings");
        const r = await plugin.openAppSettings();
        return !!(r && r.opened);
    } catch {
        return false;
    }
}
